import React from "react";
import { eur, num, stichtag } from "@/lib/format";

const BUCKETS = [0, 25, 50, 75, 100];

/**
 * Bilanz-Ansicht im Excel-Stil (User-Vorlage):
 *   - Pro Erlösgruppe 3 farbige Spalten: Lagerwert / Wertberichtigung / Bilanzwert
 *   - Zeilen: 0 / 25 / 50 / 75 / 100 %
 *   - Summenzeile unten in denselben Farben (kräftiger)
 *   - Titelanzahl als kleine Sub-Zeile in der Lagerwert-Spalte
 *   - 2 Erlösgruppen pro Reihe (wie Excel-Vorlage)
 *
 * Farben: aufgehellt aus der Excel-Vorlage:
 *   Lagerwert     #F59E0B (amber-500) / #FEF3C7 (im Print)
 *   Wertberich.   #93C5FD (blue-300)  / #DBEAFE (im Print)
 *   Bilanzwert    #86EFAC (green-300) / #DCFCE7 (im Print)
 *
 * Im Dark-Mode setzen wir die Farben dezent, damit Augen nicht bluten;
 * via @media print werden sie auf die hellen Excel-Töne umgeschaltet
 * (siehe `print.css`).
 */
export default function BilanzView({ snapshot }) {
    if (!snapshot) return null;
    const stag = stichtag(snapshot.zeitraum, snapshot.zeitraum_typ);

    // Erlösgruppen ermitteln (sortiert) + Lookup pro (eg, bucket)
    const erloesgruppen = Array.from(
        new Set((snapshot.aufschluesselung || []).map((r) => r.erloesgruppe)),
    ).sort();

    const lookup = {};
    for (const r of snapshot.aufschluesselung || []) {
        lookup[`${r.erloesgruppe}::${r.wb_bucket}`] = r;
    }

    // Erlösgruppen in 2er-Paaren rendern (wie Excel-Vorlage)
    const paare = [];
    for (let i = 0; i < erloesgruppen.length; i += 2) {
        paare.push([erloesgruppen[i], erloesgruppen[i + 1] || null]);
    }

    return (
        <div className="space-y-8 bilanz-view" data-testid="snap-bilanz-view">
            {paare.map((paar, idx) => (
                <div key={idx} className="grid lg:grid-cols-2 gap-8 print:grid-cols-2 print:gap-6">
                    {paar.map((eg) => (
                        <ErloesgruppeBlock
                            key={eg || `empty-${idx}`}
                            erloesgruppe={eg}
                            lookup={lookup}
                            stichtag={stag}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

function ErloesgruppeBlock({ erloesgruppe, lookup, stichtag: stag }) {
    if (!erloesgruppe) return <div />;

    // Kürzel für die Excel-Header-Kennung (z. B. "3: Annette Betz" → "AB")
    const kuerzel = abbrev(erloesgruppe);

    // Summen über alle Buckets dieser Erlösgruppe
    let sumLager = 0, sumWB = 0, sumBilanz = 0, sumTitel = 0, sumStk = 0;
    for (const b of BUCKETS) {
        const r = lookup[`${erloesgruppe}::${b}`];
        if (r) {
            sumLager += r.lagerwert || 0;
            sumWB += r.wertberichtigung || 0;
            sumBilanz += r.bilanzwert || 0;
            sumTitel += r.anzahl_titel || 0;
            sumStk += r.lager_gesamt || 0;
        }
    }

    return (
        <div className="bilanz-block" data-testid={`bilanz-block-${erloesgruppe}`}>
            {/* Erlösgruppen-Titel oben */}
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1.5 print:text-black">
                {erloesgruppe}
            </div>

            <table className="bilanz-table w-full border-collapse text-sm tabular">
                <thead>
                    <tr>
                        <th className="bilanz-th-empty" />
                        <th className="bilanz-th bilanz-th-stk">
                            <div className="bilanz-th-kuerzel">Stück</div>
                            <div className="bilanz-th-label">auf Lager</div>
                            <div className="bilanz-th-date">&nbsp;</div>
                        </th>
                        <th className="bilanz-th bilanz-th-lager">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Lagerwert zum</div>
                            <div className="bilanz-th-date">{stag}</div>
                        </th>
                        <th className="bilanz-th bilanz-th-wb">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Wertberichtigung</div>
                            <div className="bilanz-th-date">zum {stag}</div>
                        </th>
                        <th className="bilanz-th bilanz-th-bilanz">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Bilanzwert zum</div>
                            <div className="bilanz-th-date">{stag}</div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {BUCKETS.map((b) => {
                        const r = lookup[`${erloesgruppe}::${b}`];
                        return (
                            <tr key={b}>
                                <td className="bilanz-row-label">{b}%</td>
                                <td className="bilanz-td bilanz-td-stk">
                                    <div>{num(r?.lager_gesamt || 0)}</div>
                                    {r?.anzahl_titel ? (
                                        <div className="bilanz-td-sub">{num(r.anzahl_titel)} Titel</div>
                                    ) : null}
                                </td>
                                <td className="bilanz-td bilanz-td-lager">{eur(r?.lagerwert || 0)}</td>
                                <td className="bilanz-td bilanz-td-wb">{eur(r?.wertberichtigung || 0)}</td>
                                <td className="bilanz-td bilanz-td-bilanz">{eur(r?.bilanzwert || 0)}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td className="bilanz-foot-label">Σ</td>
                        <td className="bilanz-foot bilanz-foot-stk">
                            <div>{num(sumStk)}</div>
                            {sumTitel ? <div className="bilanz-td-sub">Σ {num(sumTitel)} Titel</div> : null}
                        </td>
                        <td className="bilanz-foot bilanz-foot-lager">{eur(sumLager)}</td>
                        <td className="bilanz-foot bilanz-foot-wb">{eur(sumWB)}</td>
                        <td className="bilanz-foot bilanz-foot-bilanz">{eur(sumBilanz)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

// Erzeugt ein Kürzel aus dem Erlösgruppen-Namen.
// "3: Annette Betz"         → "AB"
// "4: Ueberreuter Kinder- und Jugendbuch" → "KJ" via Sonderfall, sonst Initialen
function abbrev(name) {
    if (!name) return "";
    const cleaned = String(name).replace(/^\d+\s*:\s*/, "").trim();
    // Sonderfall „Kinder- und Jugend…" → KJ (User-Vorlage)
    if (/kinder.*jugend/i.test(cleaned)) return "KJ";
    if (/annette\s*betz/i.test(cleaned)) return "AB";
    const words = cleaned.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return cleaned.slice(0, 2).toUpperCase();
}
